import { useState } from 'react';
import { api, ApiError, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useRefData } from '../../lib/refdata';
import type { EstablishmentSummary, Grade, PayComponent, PayrollEmployee } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

function sessionList<T>(key: string): T[] {
  const raw = sessionStorage.getItem(key);
  return raw ? (JSON.parse(raw) as T[]) : [];
}

export function Establishment() {
  const { persona } = useAuth();
  const { votes } = useRefData();
  const isDpa = persona?.roles.includes('DPA_PAYROLL_ADMIN') ?? false;
  const isHr = persona?.roles.includes('MDA_HR_OFFICER') ?? false;

  const [voteId, setVoteId] = useState('');
  const [showGrade, setShowGrade] = useState(false);
  const [showComponent, setShowComponent] = useState(false);
  const [showPitBands, setShowPitBands] = useState(false);
  const [showSpfRate, setShowSpfRate] = useState(false);
  const [showPost, setShowPost] = useState(false);
  const [fillTarget, setFillTarget] = useState<EstablishmentSummary['items'][number] | null>(null);
  const [showGarnishment, setShowGarnishment] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [grades, setGrades] = useState<Grade[]>(() => sessionList('govfin.payrollGrades'));
  const [components, setComponents] = useState<PayComponent[]>(() => sessionList('govfin.payrollComponents'));
  const [filledEmployees, setFilledEmployees] = useState<PayrollEmployee[]>(() => sessionList('govfin.payrollEmployees'));

  const persistGrades = (items: Grade[]) => {
    setGrades(items);
    sessionStorage.setItem('govfin.payrollGrades', JSON.stringify(items));
  };
  const persistComponents = (items: PayComponent[]) => {
    setComponents(items);
    sessionStorage.setItem('govfin.payrollComponents', JSON.stringify(items));
  };
  const persistEmployees = (items: PayrollEmployee[]) => {
    setFilledEmployees(items);
    sessionStorage.setItem('govfin.payrollEmployees', JSON.stringify(items));
  };

  const establishmentQ = useApiQuery<EstablishmentSummary>(voteId ? `/api/v1/payroll/establishment/${voteId}` : null);

  const freezePost = async (postId: string) => {
    setActionError(null);
    try {
      await api.post(`/api/v1/payroll/posts/${postId}/freeze`);
      establishmentQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Freeze failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Establishment & Payroll Configuration"
        subtitle="Approved posts are the payroll's own commitment control — no pay without an approved post"
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      {isDpa ? (
        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <button className="card p-4 text-left hover:bg-slate-50" onClick={() => setShowGrade(true)}>
            <div className="text-sm font-semibold text-slate-700">+ Pay grade</div>
            <div className="text-xs text-slate-400">Basic salary & eligible allowances</div>
          </button>
          <button className="card p-4 text-left hover:bg-slate-50" onClick={() => setShowComponent(true)}>
            <div className="text-sm font-semibold text-slate-700">+ Pay component</div>
            <div className="text-xs text-slate-400">Allowance / deduction calc rule</div>
          </button>
          <button className="card p-4 text-left hover:bg-slate-50" onClick={() => setShowPitBands(true)}>
            <div className="text-sm font-semibold text-slate-700">Set PIT bands</div>
            <div className="text-xs text-slate-400">Progressive monthly income tax</div>
          </button>
          <button className="card p-4 text-left hover:bg-slate-50" onClick={() => setShowSpfRate(true)}>
            <div className="text-sm font-semibold text-slate-700">Set SPF rate</div>
            <div className="text-xs text-slate-400">Employee + employer contribution %</div>
          </button>
        </div>
      ) : null}

      {grades.length > 0 ? (
        <div className="mb-6">
          <h2 className="mb-2 text-sm font-bold text-slate-700">Pay grades (this session)</h2>
          <p className="mb-2 text-xs text-slate-400">There is no GET /grades listing endpoint yet — grades you register in this tab are remembered here.</p>
          <div className="card overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Code</th>
                  <th className="table-head">Name</th>
                  <th className="table-head text-right">Basic salary</th>
                  <th className="table-head">Allowances</th>
                </tr>
              </thead>
              <tbody>
                {grades.map((g) => (
                  <tr key={g.id}>
                    <td className="table-cell font-medium">{g.code}</td>
                    <td className="table-cell">{g.name}</td>
                    <td className="table-cell text-right tabular-nums">{scr(g.basicSalary)}</td>
                    <td className="table-cell text-xs text-slate-500">{g.allowancesEligible.join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <div className="mb-6">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-bold text-slate-700">Establishment</h2>
          <div className="flex items-center gap-2">
            <select className="field-input !py-1 text-xs" value={voteId} onChange={(e) => setVoteId(e.target.value)}>
              <option value="">Select vote…</option>
              {votes.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.code} — {v.name}
                </option>
              ))}
            </select>
            {isDpa && voteId ? (
              <button className="btn-secondary !py-1 text-xs" onClick={() => setShowPost(true)}>
                + Create post
              </button>
            ) : null}
          </div>
        </div>

        {!voteId ? (
          <p className="text-xs text-slate-400">Select a ministry vote to see its establishment.</p>
        ) : (
          <>
            {establishmentQ.data ? (
              <div className="mb-3 grid gap-3 sm:grid-cols-3">
                <StatCard label="Approved posts" value={establishmentQ.data.approved} />
                <StatCard label="Filled" value={establishmentQ.data.filled} tone="good" />
                <StatCard label="Vacant" value={establishmentQ.data.vacant} tone={establishmentQ.data.vacant > 0 ? 'warn' : 'default'} />
              </div>
            ) : null}
            <div className="card overflow-x-auto">
              {establishmentQ.loading ? (
                <Spinner />
              ) : establishmentQ.error ? (
                <ErrorNote message={establishmentQ.error} />
              ) : (establishmentQ.data?.items.length ?? 0) === 0 ? (
                <EmptyState title="No posts for this vote" hint="Create a post against a grade to start building the establishment." />
              ) : (
                <table className="w-full">
                  <thead>
                    <tr>
                      <th className="table-head">Post</th>
                      <th className="table-head">Grade</th>
                      <th className="table-head">Holder</th>
                      <th className="table-head">Status</th>
                      {isDpa || isHr ? <th className="table-head" /> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {establishmentQ.data!.items.map((p) => (
                      <tr key={p.id}>
                        <td className="table-cell font-medium">{p.title}</td>
                        <td className="table-cell text-xs">
                          {p.grade?.code} · {scr(p.grade?.basicSalary)}
                        </td>
                        <td className="table-cell text-xs">{p.employee ? `${p.employee.fullName} (${p.employee.nin})` : 'Vacant'}</td>
                        <td className="table-cell">
                          <StatusBadge status={p.status} />
                        </td>
                        {isDpa || isHr ? (
                          <td className="table-cell text-right">
                            <div className="flex justify-end gap-1.5">
                              {isHr && p.status === 'APPROVED' && !p.employee ? (
                                <button className="btn-success !px-2.5 !py-1 text-xs" onClick={() => setFillTarget(p)}>
                                  Fill post
                                </button>
                              ) : null}
                              {isDpa && p.status === 'APPROVED' ? (
                                <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => freezePost(p.id)}>
                                  Freeze
                                </button>
                              ) : null}
                            </div>
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>

      {isDpa ? (
        <div className="mb-6">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-700">Garnishment orders</h2>
            <button className="btn-secondary !py-1 text-xs" onClick={() => setShowGarnishment(true)} disabled={filledEmployees.length === 0}>
              + Register order
            </button>
          </div>
          {filledEmployees.length === 0 ? (
            <p className="text-xs text-slate-400">Fill a post first — garnishment orders are registered against the employee record it creates.</p>
          ) : (
            <p className="text-xs text-slate-400">{filledEmployees.length} employee(s) known this session and available for garnishment orders.</p>
          )}
        </div>
      ) : null}

      {showGrade ? (
        <GradeModal
          onClose={() => setShowGrade(false)}
          onDone={(grade) => {
            setShowGrade(false);
            persistGrades([grade, ...grades]);
          }}
        />
      ) : null}
      {showComponent ? (
        <ComponentModal
          onClose={() => setShowComponent(false)}
          onDone={(component) => {
            setShowComponent(false);
            persistComponents([component, ...components]);
          }}
        />
      ) : null}
      {showPitBands ? <PitBandsModal onClose={() => setShowPitBands(false)} onDone={() => setShowPitBands(false)} /> : null}
      {showSpfRate ? <SpfRateModal onClose={() => setShowSpfRate(false)} onDone={() => setShowSpfRate(false)} /> : null}
      {showPost && voteId ? (
        <CreatePostModal
          voteId={voteId}
          grades={grades}
          onClose={() => setShowPost(false)}
          onDone={() => {
            setShowPost(false);
            establishmentQ.reload();
          }}
        />
      ) : null}
      {fillTarget ? (
        <FillPostModal
          post={fillTarget}
          onClose={() => setFillTarget(null)}
          onDone={(employee) => {
            setFillTarget(null);
            persistEmployees([employee, ...filledEmployees]);
            establishmentQ.reload();
          }}
        />
      ) : null}
      {showGarnishment ? (
        <GarnishmentModal
          employees={filledEmployees}
          onClose={() => setShowGarnishment(false)}
          onDone={() => setShowGarnishment(false)}
        />
      ) : null}
    </div>
  );
}

function GradeModal({ onClose, onDone }: { onClose: () => void; onDone: (grade: Grade) => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [basicSalary, setBasicSalary] = useState('');
  const [allowances, setAllowances] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const grade = await api.post<Grade>('/api/v1/payroll/grades', {
        code,
        name,
        basicSalary: Number.parseFloat(basicSalary),
        allowancesEligible: allowances
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      });
      onDone(grade);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create grade');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New pay grade" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code">
            <input className="field-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. SG7" />
          </Field>
          <Field label="Basic salary (SCR/month)">
            <input className="field-input" type="number" min="0" step="0.01" value={basicSalary} onChange={(e) => setBasicSalary(e.target.value)} />
          </Field>
        </div>
        <Field label="Name">
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Senior Grade 7" />
        </Field>
        <Field label="Eligible allowance component codes (comma-separated)">
          <input className="field-input" value={allowances} onChange={(e) => setAllowances(e.target.value)} placeholder="e.g. HOUSING, INDUCEMENT" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !code || !name || !basicSalary}>
            {busy ? 'Creating…' : 'Create grade'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ComponentModal({ onClose, onDone }: { onClose: () => void; onDone: (component: PayComponent) => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<PayComponent['type']>('ALLOWANCE');
  const [ruleKind, setRuleKind] = useState<'flat' | 'percentOfBasic'>('flat');
  const [ruleValue, setRuleValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const component = await api.post<PayComponent>('/api/v1/payroll/components', {
        code,
        name,
        type,
        calcRule: ruleKind === 'flat' ? { flat: Number.parseFloat(ruleValue) } : { percentOfBasic: Number.parseFloat(ruleValue) },
      });
      onDone(component);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create component');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New pay component" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code">
            <input className="field-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="e.g. HOUSING" />
          </Field>
          <Field label="Type">
            <select className="field-input" value={type} onChange={(e) => setType(e.target.value as PayComponent['type'])}>
              <option value="BASIC">Basic</option>
              <option value="ALLOWANCE">Allowance</option>
              <option value="DEDUCTION">Deduction</option>
              <option value="EMPLOYER_COST">Employer cost</option>
            </select>
          </Field>
        </div>
        <Field label="Name">
          <input className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Housing allowance" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Calculation rule">
            <select className="field-input" value={ruleKind} onChange={(e) => setRuleKind(e.target.value as 'flat' | 'percentOfBasic')}>
              <option value="flat">Flat amount</option>
              <option value="percentOfBasic">% of basic salary</option>
            </select>
          </Field>
          <Field label="Value">
            <input className="field-input" type="number" min="0" step="0.01" value={ruleValue} onChange={(e) => setRuleValue(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !code || !name || !ruleValue}>
            {busy ? 'Creating…' : 'Create component'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function PitBandsModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [effectiveFrom, setEffectiveFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [bands, setBands] = useState([
    { lower: '0', upper: '8555', ratePercent: '0' },
    { lower: '8555', upper: '20000', ratePercent: '15' },
    { lower: '20000', upper: '', ratePercent: '30' },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/payroll/pit-bands', {
        effectiveFrom,
        bands: bands.map((b) => ({
          lower: Number.parseFloat(b.lower),
          upper: b.upper ? Number.parseFloat(b.upper) : null,
          ratePercent: Number.parseFloat(b.ratePercent),
        })),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to set PIT bands');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Set personal income tax bands" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Effective from">
          <input className="field-input w-48" type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
        </Field>
        {bands.map((b, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <input className="field-input" type="number" placeholder="Lower" value={b.lower} onChange={(e) => setBands(bands.map((r, i) => (i === idx ? { ...r, lower: e.target.value } : r)))} />
            <span className="text-xs text-slate-400">to</span>
            <input className="field-input" type="number" placeholder="Upper (blank = top band)" value={b.upper} onChange={(e) => setBands(bands.map((r, i) => (i === idx ? { ...r, upper: e.target.value } : r)))} />
            <input className="field-input w-28" type="number" placeholder="Rate %" value={b.ratePercent} onChange={(e) => setBands(bands.map((r, i) => (i === idx ? { ...r, ratePercent: e.target.value } : r)))} />
            <button className="btn-secondary !px-2.5" onClick={() => setBands(bands.filter((_, i) => i !== idx))}>
              ✕
            </button>
          </div>
        ))}
        <button className="btn-secondary !py-1.5 text-xs" onClick={() => setBands([...bands, { lower: '', upper: '', ratePercent: '' }])}>
          + Add band
        </button>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || bands.length === 0}>
            {busy ? 'Saving…' : 'Set bands'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SpfRateModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [effectiveFrom, setEffectiveFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [employeePct, setEmployeePct] = useState('5');
  const [employerPct, setEmployerPct] = useState('5');
  const [legalRef, setLegalRef] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/payroll/spf-rates', {
        effectiveFrom,
        employeePct: Number.parseFloat(employeePct),
        employerPct: Number.parseFloat(employerPct),
        legalRef,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to set SPF rate');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Set SPF contribution rate" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-3 gap-3">
          <Field label="Effective from">
            <input className="field-input" type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
          </Field>
          <Field label="Employee %">
            <input className="field-input" type="number" min="0" max="30" step="0.1" value={employeePct} onChange={(e) => setEmployeePct(e.target.value)} />
          </Field>
          <Field label="Employer %">
            <input className="field-input" type="number" min="0" max="30" step="0.1" value={employerPct} onChange={(e) => setEmployerPct(e.target.value)} />
          </Field>
        </div>
        <Field label="Legal reference">
          <input className="field-input" value={legalRef} onChange={(e) => setLegalRef(e.target.value)} placeholder="e.g. SPF Act 2005, s.12 (2023 rate)" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !legalRef}>
            {busy ? 'Saving…' : 'Set rate'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function CreatePostModal({
  voteId,
  grades,
  onClose,
  onDone,
}: {
  voteId: string;
  grades: Grade[];
  onClose: () => void;
  onDone: () => void;
}) {
  const voteDetailQ = useApiQuery<{ programmes?: Array<{ id: string; code: string; name: string }> }>(`/api/v1/coa/votes/${voteId}`);
  const combosQ = useApiQuery<{ items: Array<{ id: string; programme?: { code: string }; econItem?: { name: string } }> }>(
    `/api/v1/coa/combinations?voteId=${voteId}&active=true&limit=100`,
  );
  const [programmeId, setProgrammeId] = useState('');
  const [title, setTitle] = useState('');
  const [gradeId, setGradeId] = useState('');
  const [coaCombinationId, setCoaCombinationId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/payroll/posts', { voteId, programmeId, title, gradeId, coaCombinationId });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create post');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Create establishment post" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        {grades.length === 0 ? (
          <div className="rounded-lg bg-amber-50 px-3.5 py-2.5 text-sm text-amber-700">Register a pay grade first.</div>
        ) : null}
        <Field label="Title">
          <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Senior Accountant" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Programme">
            <select className="field-input" value={programmeId} onChange={(e) => setProgrammeId(e.target.value)}>
              <option value="">Select…</option>
              {(voteDetailQ.data?.programmes ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} — {p.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Grade">
            <select className="field-input" value={gradeId} onChange={(e) => setGradeId(e.target.value)}>
              <option value="">Select…</option>
              {grades.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.code} — {g.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Budget line (wage cost posting dimension)">
          <select className="field-input" value={coaCombinationId} onChange={(e) => setCoaCombinationId(e.target.value)}>
            <option value="">Select…</option>
            {(combosQ.data?.items ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.programme?.code} · {c.econItem?.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !title || !programmeId || !gradeId || !coaCombinationId}>
            {busy ? 'Creating…' : 'Create post'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function FillPostModal({
  post,
  onClose,
  onDone,
}: {
  post: EstablishmentSummary['items'][number];
  onClose: () => void;
  onDone: (employee: PayrollEmployee) => void;
}) {
  const [form, setForm] = useState({ nin: '', fullName: '', startDate: new Date().toISOString().slice(0, 10), bankCode: 'SEYLSCSC', bankAccount: '', taxCode: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const employee = await api.post<PayrollEmployee>(`/api/v1/payroll/posts/${post.id}/fill`, {
        nin: form.nin,
        fullName: form.fullName,
        startDate: form.startDate,
        bankCode: form.bankCode,
        bankAccount: form.bankAccount,
        ...(form.taxCode ? { taxCode: form.taxCode } : {}),
      });
      onDone(employee);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to fill post');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Fill post — ${post.title}`} onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="NIN">
            <input className="field-input" value={form.nin} onChange={(e) => setForm({ ...form, nin: e.target.value })} placeholder="e.g. 985-0342-7-1" />
          </Field>
          <Field label="Full name">
            <input className="field-input" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Start date">
            <input className="field-input" type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} />
          </Field>
          <Field label="Bank code">
            <input className="field-input" value={form.bankCode} onChange={(e) => setForm({ ...form, bankCode: e.target.value })} />
          </Field>
          <Field label="Bank account">
            <input className="field-input" value={form.bankAccount} onChange={(e) => setForm({ ...form, bankAccount: e.target.value })} />
          </Field>
        </div>
        <Field label="Tax code (optional, default STD)">
          <input className="field-input" value={form.taxCode} onChange={(e) => setForm({ ...form, taxCode: e.target.value })} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !form.nin || !form.fullName || !form.bankAccount}>
            {busy ? 'Filling…' : 'Fill post'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function GarnishmentModal({ employees, onClose, onDone }: { employees: PayrollEmployee[]; onClose: () => void; onDone: () => void }) {
  const [employeeId, setEmployeeId] = useState('');
  const [ref, setRef] = useState('');
  const [monthlyAmount, setMonthlyAmount] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/payroll/garnishments', {
        employeeId,
        ref,
        monthlyAmount: Number.parseFloat(monthlyAmount),
        totalAmount: Number.parseFloat(totalAmount),
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register garnishment');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register garnishment order" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Employee">
          <select className="field-input" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            <option value="">Select…</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>
                {e.fullName} ({e.nin})
              </option>
            ))}
          </select>
        </Field>
        <Field label="Order reference">
          <input className="field-input" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="e.g. Court Order CR-2026-118" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Monthly amount (SCR)">
            <input className="field-input" type="number" min="0" step="0.01" value={monthlyAmount} onChange={(e) => setMonthlyAmount(e.target.value)} />
          </Field>
          <Field label="Total amount (SCR)">
            <input className="field-input" type="number" min="0" step="0.01" value={totalAmount} onChange={(e) => setTotalAmount(e.target.value)} />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !employeeId || !ref || !monthlyAmount || !totalAmount}>
            {busy ? 'Registering…' : 'Register order'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
